<template>
  <div :class="[classes.fontFillText]" ref="fontFillTextRef">
    <DynamicText
      :class="classes.fontFillTextBit"
      tag="span"
      :styleGroup="styleGroup"
      :get="getFont"
      :set="setFont"
    >
      <p :key="line" :style="fontFillStyle" v-for="line in fontFillLines">
        {{ line }}
      </p>
    </DynamicText>
  </div>
</template>

<script lang="ts">
import { useDimensionSync } from "@fresh8/vue-utils/src/dimensionSync";
import {
  defineComponent,
  watchEffect,
  ref,
  computed,
  PropType,
  onMounted
} from "vue";
import { AutoFittingText } from "fontfill/src/index";
import { injectBrand } from "@fresh8/ad-data-composition/src/brand";
import { titleCase } from "@fresh8/ad-data-composition/src/lib/formatText";
import DynamicText from "./DynamicText.vue";
import { Brand } from "@fresh8/types/src/index";
import { createUseStyles } from "@fresh8/vue-jss/src";

const useStyles = createUseStyles({
  fontFillText: {
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
    position: "relative",
    justifyContent: props => props.justifyContent,
    alignItems: props => props.alignment
  },
  fontFillTextBit: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    justifyContent: props => props.justifyContent,
    alignItems: props => props.alignment,
    // This fixes an issue with some fonts loading in Safari
    // bigger than when they measured by fontfill in canvas
    textRendering: "optimizeSpeed"
  }
});

export default defineComponent({
  components: { DynamicText },
  props: {
    text: String,
    styleGroup: { type: String, required: true },
    maxSize: {
      type: Number,
      default: 25
    },
    minSize: {
      type: Number,
      default: 10
    },
    lineHeight: {
      type: Number,
      default: 1.2
    },
    getFont: {
      type: Function as PropType<
        (brand: Brand | undefined) => string | undefined
      >,
      required: true
    },
    setFont: {
      type: Function as PropType<(brand: Brand, value: string) => void>,
      required: true
    },
    alignment: {
      type: String as PropType<"flex-end" | "flex-start" | "center">,
      default: "center"
    },
    justifyContent: {
      type: String as PropType<"flex-end" | "flex-start" | "center">,
      default: "center"
    }
  },
  setup(props) {
    const reactiveStyles = computed(() => ({
      alignment: props.alignment,
      justifyContent: props.justifyContent
    }));

    const classes = useStyles(reactiveStyles);
    const brandContext = injectBrand();

    const fontId = computed(
      () => props.getFont(brandContext?.brand.value) || ""
    );

    const fontStyle = computed(() =>
      brandContext?.getFontStyle(fontId.value, true)
    );

    const defaultFontFamily =
      "'Roboto','Arial','Helvetica Neue','Helvetica',sans-serif";

    const targetString = computed(() => {
      if (!props.text) {
        return "";
      }

      if (fontStyle?.value?.textTransform === "uppercase") {
        return props.text.toUpperCase().trim();
      }

      if (fontStyle?.value?.textTransform === "capitalize") {
        return titleCase(props.text).trim();
      }

      if (fontStyle?.value?.textTransform === "lowercase") {
        return props.text.toLowerCase().trim();
      }

      return props.text.trim();
    });

    const fontFillTextRef = ref();
    const { dimensions: fontFillTextBox, layoutContext } = useDimensionSync(
      fontFillTextRef
    );
    const fontFitter = new AutoFittingText(
      fontFillTextBox.value.width,
      fontFillTextBox.value.height,
      {
        targetString: targetString.value,
        family: fontStyle.value?.fontFamily || defaultFontFamily,
        lineHeight: props.lineHeight,
        maxFontSize: props.maxSize,
        minFontSize: props.minSize
      }
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fontMetrics = ref<any>();

    watchEffect(() => {
      fontFitter.width = fontFillTextBox.value.width;
      fontFitter.height = fontFillTextBox.value.height;

      fontFitter.targetString = targetString.value;
      fontFitter.family = fontStyle.value?.fontFamily || defaultFontFamily;

      window.requestAnimationFrame(() => {
        fontMetrics.value = fontFitter.metrics;
      });

      document.fonts &&
        document.fonts
          .load(`12px ${fontStyle.value?.fontFamily}`, fontFitter.targetString)
          .then(() => {
            // trigger a recalculation
            // should make fontfill more functional
            fontFitter.family = "";
            fontFitter.family =
              fontStyle.value?.fontFamily || defaultFontFamily;
            fontMetrics.value = fontFitter.metrics;
            layoutContext.layoutEffect();
          });
    });

    const fontFillLines = computed(() => fontMetrics?.value?.lines as string[]);
    const fontFillStyle = computed(() => ({
      fontSize: Math.floor(fontMetrics.value.fontSize) + "px",
      lineHeight: props.lineHeight,
      margin: 0,
      padding: 0
    }));

    const rafId = ref();

    function refreshFontFill() {
      layoutContext.layoutEffect();
      rafId.value = requestAnimationFrame(refreshFontFill);
    }

    onMounted(() => {
      rafId.value = requestAnimationFrame(refreshFontFill);

      setTimeout(() => {
        cancelAnimationFrame(rafId.value);
      }, 1000);
    });

    return {
      classes,
      fontFillTextRef,
      fontFillLines,
      fontFillStyle
    };
  }
});
</script>

<style></style>
